import { FC, Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Header from "./Header"
import { formatCustomers } from "./utils"
import CustomersFacilitiesTable, {
   Customer as CustomerTable,
} from "@/components/CustomersFacilitiesTable"
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal"
import Loader from "@/components/Loader"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { SpinnerSize } from "@/components/shared/Spinner"
import { HttpCodes } from "@/constants/globals"
import { useErrorMessage } from "@/hooks/useErrorMessage"
import useModal from "@/hooks/useModal"
import { AppDispatch, RootState } from "@/store"
import {
   fetchCustomers,
   createCustomer,
   deleteCustomer,
} from "@/store/customersSlice"
import { createFacility, deleteFacility } from "@/store/facilitiesSlice"
import { CustomerForm } from "@/types/customer"
import { FacilityForm } from "@/types/facility"
import "./index.scss"

const ControlPanel: FC = () => {
   const {
      isOpen: isDeleteConfirmationModalOpen,
      openModal,
      closeModal,
   } = useModal()
   const dispatch = useDispatch<AppDispatch>()
   const {
      loading: loadingUser,
      tenants,
      selectedTenantId,
   } = useSelector(({ userReducer }: RootState) => userReducer)
   const { loading, loadingCustomer, customers, error } = useSelector(
      ({ customersReducer }: RootState) => customersReducer
   )
   const [customersData, setCustomersData] = useState<CustomerTable[]>([])
   const [filteredCustomersData, setFilteredCustomersData] = useState<
      CustomerTable[]
   >([])
   const [searchValue, setSearchValue] = useState<string>("")
   const [deletionSuccess, setDeletionSuccess] = useState<boolean>(false)
   const [{ deletionKind, id, text }, setCustomerDeletionData] = useState<{
      deletionKind: string
      id: string
      text: string
   }>({ deletionKind: "", id: "", text: "" })
   const [createCustomerMode, setCreateCustomerMode] = useState<boolean>(false)
   const [createFacilityMode, setCreateFacilityMode] = useState<boolean>(false)
   const [isEditingRow, setIsEditingRow] = useState<boolean>(false)

   const errorMessage = useErrorMessage([error])

   const isEmptyData = useMemo(() => {
      return customersData.length === 0
   }, [customersData])

   useEffect(() => {
      async function fetchCustomersData() {
         try {
            await dispatch(fetchCustomers()).unwrap()
         } catch (err) {
            console.error("Customers data fetch failed:", err)
         }
      }
      if (!loadingUser && tenants.length > 0 && selectedTenantId) {
         fetchCustomersData()
      }
   }, [loadingUser, dispatch, tenants.length, selectedTenantId])

   useEffect(() => {
      if (!loading && customers) {
         const formattedCustomers = formatCustomers(customers)
         setCustomersData(formattedCustomers)
         setFilteredCustomersData(formattedCustomers)
      }
   }, [loading, customers])

   useEffect(() => {
      if (customersData) {
         const value = searchValue.trim().toLowerCase()
         if (value === "") {
            setFilteredCustomersData(customersData)
         } else {
            setFilteredCustomersData(
               customersData.filter(
                  (customer) =>
                     customer.customerName?.toLowerCase().includes(value) ||
                     customer.facilities.some((facility) =>
                        facility.name?.toLowerCase().includes(value)
                     )
               )
            )
         }
      }
   }, [searchValue, customersData])

   useEffect(() => {
      if (deletionSuccess) {
         setIsEditingRow(false)
      }
   }, [deletionSuccess])

   const handleCustomerCreation = useCallback(
      async (customerForm: CustomerForm) => {
         try {
            await dispatch(createCustomer(customerForm))
               .unwrap()
               .then(() => {
                  setCreateCustomerMode(false)
               })
         } catch (err) {
            console.error("Customer creation failed:", err)
         }
      },
      [dispatch]
   )

   const openDeleteConfirmation = useCallback(
      (deletionKind: "customer" | "facility", id: string, text: string) => {
         setCustomerDeletionData({
            deletionKind,
            id,
            text,
         })
         openModal()
      },
      [openModal]
   )

   const handleFacilityCreation = useCallback(
      async (customerId: string, facilityForm: FacilityForm) => {
         try {
            await dispatch(
               createFacility({ customerId, facilityData: facilityForm })
            )
               .unwrap()
               .then(() => {
                  setCreateFacilityMode(false)
               })
         } catch (err) {
            console.error("Facility creation failed:", err)
         }
      },
      [dispatch]
   )

   const handleClose = useCallback(() => {
      setDeletionSuccess(false)
      closeModal()
   }, [closeModal])

   const handleCustomerDeletion = useCallback(
      async (customerId: string) => {
         try {
            const result = await dispatch(deleteCustomer(customerId)).unwrap()
            if (result.status === HttpCodes.NO_CONTENT) {
               setDeletionSuccess(true)
            }
         } catch (err) {
            console.error("Customer deletion failed:", err)
            handleClose()
         }
      },
      [dispatch, handleClose]
   )

   const handleFacilityDeletion = useCallback(
      async (customerId: string) => {
         try {
            const result = await dispatch(deleteFacility(customerId)).unwrap()
            if (result.status === HttpCodes.NO_CONTENT) {
               setDeletionSuccess(true)
            }
         } catch (err) {
            console.error("Facility deletion failed:", err)
            handleClose()
         }
      },
      [dispatch, handleClose]
   )

   return (
      <>
         <Grid
            direction="column"
            flexWrap="nowrap"
         >
            <Header
               addDisabled={
                  createCustomerMode ||
                  createFacilityMode ||
                  isEditingRow ||
                  !!errorMessage
               }
               setCreateCustomerMode={setCreateCustomerMode}
               setSearchValue={setSearchValue}
            />
            <GridItem size={12}>
               <Suspense fallback={<Loader />}>
                  {loading ? (
                     <Loader
                        size={SpinnerSize.LARGE}
                        fullHeight={false}
                     />
                  ) : (
                     <CustomersFacilitiesTable
                        filteredData={filteredCustomersData}
                        isEmptyData={isEmptyData}
                        error={errorMessage}
                        createCustomerMode={createCustomerMode}
                        createFacilityMode={createFacilityMode}
                        rowDeletionSuccess={deletionSuccess}
                        cancelCustomerCreation={() =>
                           setCreateCustomerMode(false)
                        }
                        onCreateFacility={() => {
                           setCreateFacilityMode(true)
                        }}
                        cancelFacilityCreation={() =>
                           setCreateFacilityMode(false)
                        }
                        onSaveCustomer={handleCustomerCreation}
                        onDeleteCustomer={(customerId, customerName) =>
                           openDeleteConfirmation(
                              "customer",
                              customerId,
                              customerName
                           )
                        }
                        onDeleteFacility={(facilityId, facilityName) =>
                           openDeleteConfirmation(
                              "facility",
                              facilityId,
                              facilityName
                           )
                        }
                        onAddFacility={handleFacilityCreation}
                        onEditRowChange={(isEditingRow) =>
                           setIsEditingRow(isEditingRow)
                        }
                     />
                  )}
               </Suspense>
            </GridItem>
         </Grid>
         {isDeleteConfirmationModalOpen && (
            <DeleteConfirmationModal
               kind={deletionKind}
               text={text}
               isSuccess={deletionSuccess}
               loading={loadingCustomer}
               handleSave={() =>
                  deletionKind === "customer"
                     ? handleCustomerDeletion(id)
                     : handleFacilityDeletion(id)
               }
               handleCloseModal={handleClose}
            />
         )}
      </>
   )
}
export default ControlPanel
